import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnInit,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TabsModule } from 'primeng/tabs';
import { ParamService } from '@monsido/core/param/param.service';
import {
    SiteAnalyticsScaffoldConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import {
    EVENTS_CATEGORIES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/events-categories.config';
import {
    EVENTS_ACTIONS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/events-actions.config';
import {
    EVENTS_NAMES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/events-names.config';
import {
    StatisticsPageScaffoldComponent,
} from '@client/app/feature-modules/site-analytics/components/statistics-page-scaffold/statistics-page-scaffold.component';

type EventsTabValue = 'categories' | 'actions' | 'names';

type EventsTabConfig = {
    value: EventsTabValue;
    labelKey: string;
    config: SiteAnalyticsScaffoldConfig;
};

@Component({
    selector: 'mon-statistics-events-page',
    standalone: true,
    templateUrl: './statistics-events-page.component.html',
    styleUrls: ['./statistics-events-page.component.scss'],
    imports: [
        CommonModule,
        TranslocoModule,
        TabsModule,
        StatisticsPageScaffoldComponent,
    ],
})
export class StatisticsEventsPageComponent implements OnInit {
    private readonly paramService = inject(ParamService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    readonly tabs: EventsTabConfig[] = [
        {
            value: 'categories',
            labelKey: 'siteAnalytics.events.tabs.categories',
            config: EVENTS_CATEGORIES_PAGE_CONFIG,
        },
        {
            value: 'actions',
            labelKey: 'siteAnalytics.events.tabs.actions',
            config: EVENTS_ACTIONS_PAGE_CONFIG,
        },
        {
            value: 'names',
            labelKey: 'siteAnalytics.events.tabs.names',
            config: EVENTS_NAMES_PAGE_CONFIG,
        },
    ];

    activeTab: EventsTabValue = 'categories';

    get activeConfig (): SiteAnalyticsScaffoldConfig {
        return this.getConfigForTab(this.activeTab);
    }

    ngOnInit (): void {
        const params = this.paramService.getParams() as Record<string, unknown>;
        const initialTab = this.parseTab(params.tab);

        this.setActiveTab(initialTab);

        const initialTabConfig = this.getConfigForTab(initialTab);
        const currentSortBy = typeof params.sort_by === 'string' ? params.sort_by : '';
        const currentSortDir = params.sort_dir === 'asc' || params.sort_dir === 'desc'
            ? params.sort_dir
            : initialTabConfig.defaultSortDir;

        if (params.tab !== initialTab || currentSortBy !== initialTabConfig.defaultSortBy) {
            this.paramService.updateParams({
                tab: initialTab,
                sort_by: initialTabConfig.defaultSortBy,
                sort_dir: currentSortDir,
                page: 1,
            });
        }

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((updated) => {
            const nextTab = this.parseTab(updated.tab);

            this.setActiveTab(nextTab);
        }) as (() => void);

        this.destroyRef.onDestroy(() => {
            deregisterHook();
        });
    }

    onTabChange (tabValue: string | number): void {
        const nextTab = this.parseTab(tabValue);

        if (this.activeTab === nextTab) {
            return;
        }

        this.setActiveTab(nextTab);

        const tabConfig = this.getConfigForTab(nextTab);

        this.paramService.updateParams({
            tab: nextTab,
            page: 1,
            sort_by: tabConfig.defaultSortBy,
            sort_dir: tabConfig.defaultSortDir,
        });
    }

    private parseTab (value: unknown): EventsTabValue {
        if (value === 'categories' || value === 'actions' || value === 'names') {
            return value;
        }

        if (value === '0' || value === 0) {
            return 'categories';
        }

        if (value === '1' || value === 1) {
            return 'actions';
        }

        if (value === '2' || value === 2) {
            return 'names';
        }

        return 'categories';
    }

    private getConfigForTab (tab: EventsTabValue): SiteAnalyticsScaffoldConfig {
        return this.tabs.find((entry) => entry.value === tab)?.config || EVENTS_CATEGORIES_PAGE_CONFIG;
    }

    private setActiveTab (tab: EventsTabValue): void {
        if (this.activeTab === tab) {
            return;
        }

        this.activeTab = tab;
        this.changeDetectorRef.detectChanges();
    }
}
